import { Injectable, inject } from '@angular/core';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root'
})
export class DebugLoggerService {

  private sessionService = inject(SessionService);

  private readonly AUTHORIZED_USERS = [
    'gabriele.gualtieri',
    'andrea',
    'nicole.cortez',
    'claudio.biagioli'
  ];

  private readonly STYLES = {
    http: 'color: #2196F3; font-weight: bold;',        // Blue
    action: 'color: #FF9800; font-weight: bold;',      // Orange
    state: 'color: #9C27B0; font-weight: bold;',       // Purple
    navigation: 'color: #4CAF50; font-weight: bold;',  // Green
    event: 'color: #00BCD4; font-weight: bold;',       // Cyan
    lifecycle: 'color: #607D8B; font-weight: bold;',   // Grey
    error: 'color: #F44336; font-weight: bold;',       // Red
    label: 'color: #333; font-weight: normal;',
  };

  private _enabled: boolean | null = null;
  private _headerPrinted = false;

  private get isEnabled(): boolean {
    if (this._enabled !== null) return this._enabled;

    const userName = this.sessionService.userName;
    if (!userName) return false;

    this._enabled = this.AUTHORIZED_USERS.includes(userName.toLowerCase());

    if (this._enabled && !this._headerPrinted) {
      this._headerPrinted = true;
      console.log(
        '%c AmSuite Debug Logger %c Active for: %c' + userName,
        'background: #1976D2; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
        'color: #666;',
        'color: #1976D2; font-weight: bold;'
      );
    }

    return this._enabled;
  }

  /** Reset cached state (call after login/logout) */
  reset(): void {
    this._enabled = null;
    this._headerPrinted = false;
  }

  /** Log HTTP request/response */
  logHttp(method: string, url: string, body?: any, response?: any, durationMs?: number): void {
    if (!this.isEnabled) return;

    const shortUrl = this.shortenUrl(url);
    const duration = durationMs ? ` (${durationMs}ms)` : '';

    console.groupCollapsed(
      `%c[HTTP ${method}]%c ${shortUrl}${duration}`,
      this.STYLES.http, this.STYLES.label
    );
    if (body !== undefined && body !== null) {
      console.log('%cRequest Body:', 'font-weight: bold;', body);
    }
    if (response !== undefined) {
      console.log('%cResponse:', 'font-weight: bold;', response);
    }
    console.groupEnd();
  }

  /** Log HTTP error */
  logHttpError(method: string, url: string, error: any, durationMs?: number): void {
    if (!this.isEnabled) return;

    const shortUrl = this.shortenUrl(url);
    const duration = durationMs ? ` (${durationMs}ms)` : '';

    console.groupCollapsed(
      `%c[HTTP ${method} ERROR]%c ${shortUrl}${duration} - Status: ${error?.status || 'unknown'}`,
      this.STYLES.error, this.STYLES.label
    );
    console.error('Error:', error);
    console.groupEnd();
  }

  /** Log user action (save, delete, select, toggle, etc.) */
  logAction(component: string, action: string, data?: any): void {
    if (!this.isEnabled) return;

    if (data !== undefined) {
      console.groupCollapsed(
        `%c[Action]%c [${component}] ${action}`,
        this.STYLES.action, this.STYLES.label
      );
      console.log('%cData:', 'font-weight: bold;', data);
      console.groupEnd();
    } else {
      console.log(
        `%c[Action]%c [${component}] ${action}`,
        this.STYLES.action, this.STYLES.label
      );
    }
  }

  /** Log state change (BehaviorSubject, property, session) */
  logState(component: string, property: string, value: any): void {
    if (!this.isEnabled) return;

    console.groupCollapsed(
      `%c[State]%c [${component}] ${property}`,
      this.STYLES.state, this.STYLES.label
    );
    console.log('%cValue:', 'font-weight: bold;', value);
    console.groupEnd();
  }

  /** Log navigation */
  logNavigation(destination: string, params?: any): void {
    if (!this.isEnabled) return;

    if (params !== undefined) {
      console.groupCollapsed(
        `%c[Navigation]%c -> ${destination}`,
        this.STYLES.navigation, this.STYLES.label
      );
      console.log('%cParams:', 'font-weight: bold;', params);
      console.groupEnd();
    } else {
      console.log(
        `%c[Navigation]%c -> ${destination}`,
        this.STYLES.navigation, this.STYLES.label
      );
    }
  }

  /** Log component event (Input/Output, EventEmitter, SignalR) */
  logEvent(component: string, eventName: string, data?: any): void {
    if (!this.isEnabled) return;

    if (data !== undefined) {
      console.groupCollapsed(
        `%c[Event]%c [${component}] ${eventName}`,
        this.STYLES.event, this.STYLES.label
      );
      console.log('%cData:', 'font-weight: bold;', data);
      console.groupEnd();
    } else {
      console.log(
        `%c[Event]%c [${component}] ${eventName}`,
        this.STYLES.event, this.STYLES.label
      );
    }
  }

  /** Log lifecycle hook */
  logLifecycle(component: string, hook: string): void {
    if (!this.isEnabled) return;
    console.log(
      `%c[Lifecycle]%c [${component}] ${hook}`,
      this.STYLES.lifecycle, this.STYLES.label
    );
  }

  /** Log error */
  logError(component: string, message: string, error?: any): void {
    if (!this.isEnabled) return;

    console.groupCollapsed(
      `%c[Error]%c [${component}] ${message}`,
      this.STYLES.error, this.STYLES.label
    );
    if (error) console.error(error);
    console.groupEnd();
  }

  private shortenUrl(url: string): string {
    try {
      const u = new URL(url);
      return u.pathname + u.search;
    } catch {
      return url;
    }
  }
}
